import { useEffect, useState } from "react";
import { CalendarDays, List } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CrudPage } from "../components/CrudPage";
import { ImportExcel } from "../components/ImportExcel";
import ScheduleCalendar from "../components/ScheduleCalendar";
import { PageHeader } from "../components/bits";

const DAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

export default function Schedules() {
  const { user } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [view, setView] = useState(localStorage.getItem("schedulesView") || "calendar");
  const canWrite = ["admin", "secretaria"].includes(user?.role);

  useEffect(() => { api.get("/r/subjects").then((r) => setSubjects(r.data)).catch(() => {}); }, []);

  const switchView = (v) => {
    setView(v);
    localStorage.setItem("schedulesView", v);
  };

  const toggle = (
    <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="schedules-view-toggle">
      <button
        data-testid="schedules-view-calendar"
        onClick={() => switchView("calendar")}
        className={`px-3 py-2 text-sm transition-colors flex items-center gap-1 ${view === "calendar" ? "bg-blue-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
      >
        <CalendarDays className="w-4 h-4" /> Calendario
      </button>
      <button
        data-testid="schedules-view-list"
        onClick={() => switchView("table")}
        className={`px-3 py-2 text-sm transition-colors flex items-center gap-1 ${view === "table" ? "bg-blue-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
      >
        <List className="w-4 h-4" /> Lista
      </button>
    </div>
  );

  const importBtn = canWrite && (
    <ImportExcel
      title="Importar horarios desde Excel"
      endpoint="/schedules/bulk"
      templateEndpoint="/schedules/template"
      templateName="plantilla_horarios.xlsx"
      columns={["curso", "seccion", "materia", "dia", "hora_inicio", "hora_fin", "maestro"]}
      onDone={() => setRefreshKey((k) => k + 1)}
      testid="schedules"
    />
  );

  if (view === "calendar") {
    return (
      <div data-testid="schedules-calendar-page">
        <PageHeader title="Horarios de clases" subtitle="Calendario semanal interactivo — arrastra para mover, estira para ajustar duración, clic para editar">
          {importBtn}
          {toggle}
        </PageHeader>
        <ScheduleCalendar canWrite={canWrite} refreshKey={refreshKey} />
      </div>
    );
  }

  return (
    <CrudPage
      key={refreshKey}
      title="Horarios de clases"
      subtitle="Horario por curso, materia y día — importa todo desde Excel"
      endpoint="schedules"
      testid="schedules"
      canWrite={canWrite}
      canDelete={["admin"].includes(user?.role)}
      headerExtra={<>{importBtn}{toggle}</>}
      columns={[
        { key: "course", label: "Curso", render: (s) => <span className="font-medium text-slate-900">{s.course}</span> },
        { key: "section", label: "Sección" },
        { key: "subject_id", label: "Materia", render: (s) => s.subject_name || subjects.find((x) => x.id === s.subject_id)?.name || "—" },
        { key: "day", label: "Día" },
        { key: "start_time", label: "Horario", render: (s) => <span className="font-mono text-xs">{s.start_time} – {s.end_time}</span> },
        { key: "teacher_name", label: "Maestro" },
      ]}
      fields={[
        { key: "course", label: "Curso", type: "select-api", source: "r/courses", required: true },
        { key: "section", label: "Sección", type: "select", options: [{ value: "A", label: "A" }, { value: "B", label: "B" }] },
        { key: "subject_id", label: "Materia", type: "select-api", source: "r/subjects", required: true },
        { key: "day", label: "Día", type: "select", options: DAYS.map((d) => ({ value: d, label: d })), required: true },
        { key: "start_time", label: "Hora inicio (ej: 08:00)", type: "text" },
        { key: "end_time", label: "Hora fin (ej: 09:30)", type: "text" },
        { key: "teacher_name", label: "Maestro", type: "text" },
      ]}
      transformPayload={(p) => {
        const subj = subjects.find((s) => s.id === p.subject_id);
        return { ...p, subject_name: subj?.name || "" };
      }}
    />
  );
}
